const asArray = (value) => (Array.isArray(value) ? value : [])

const normalizeText = (value) =>
  String(value ?? '')
    .trim()
    .toLocaleLowerCase()

const getDefinitionKey = (item = {}) => item.uid || item.id || null

const isAnsweredResponse = (item = {}) =>
  item.answered === true ||
  Boolean(item.response_uid) ||
  String(item.answer ?? '').trim() !== '' ||
  asArray(item.image_answer).length > 0

const getAnswerValues = (response = {}) => {
  const selectedOptionIds = asArray(response.selected_option_ids).filter(Boolean)
  const answerValues = Array.isArray(response.answer)
    ? response.answer
    : String(response.answer ?? '')
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean)

  return { selectedOptionIds, answerValues }
}

const responseMatchesDefinition = (response, definition) => {
  const responseDefinitionUid = response.survey_item_uid || response.survey_uid
  const definitionKey = getDefinitionKey(definition)

  if (responseDefinitionUid && definitionKey) {
    return responseDefinitionUid === definitionKey
  }

  if (normalizeText(response.question) !== normalizeText(definition.question)) {
    return false
  }

  // Varias ramas condicionales pueden repetir la misma pregunta, así que cuando la
  // respuesta trae su origen hay que respetarlo para no mezclar ramas.
  if (response.parent_survey_uid && definition.parent_survey_uid) {
    if (response.parent_survey_uid !== definition.parent_survey_uid) {
      return false
    }
  }

  if (response.parent_option_id && definition.parent_option_id) {
    return response.parent_option_id === definition.parent_option_id
  }

  return true
}

const findResponse = (definition, responses, usedResponses) => {
  const exactUidIndex = responses.findIndex((response, index) => {
    if (usedResponses.has(index)) return false
    const responseDefinitionUid = response.survey_item_uid || response.survey_uid
    return responseDefinitionUid && responseDefinitionUid === getDefinitionKey(definition)
  })

  if (exactUidIndex >= 0) {
    usedResponses.add(exactUidIndex)
    return responses[exactUidIndex]
  }

  const questionIndex = responses.findIndex(
    (response, index) =>
      !usedResponses.has(index) && responseMatchesDefinition(response, definition)
  )

  if (questionIndex >= 0) {
    usedResponses.add(questionIndex)
    return responses[questionIndex]
  }

  return null
}

const DEFINITION_FIELDS = [
  'question',
  'descriptions',
  'type',
  'image_requirement',
  'display_index',
  'level',
  'parent_survey_uid',
  'parent_option_id',
  'parent_option_text'
]

const isEmptyValue = (value) =>
  value === undefined || value === null || (typeof value === 'string' && value.trim() === '')

const completeDefinition = (definition, extraDefinition) => {
  const completed = { ...definition }

  DEFINITION_FIELDS.forEach((field) => {
    if (isEmptyValue(completed[field]) && !isEmptyValue(extraDefinition?.[field])) {
      completed[field] = extraDefinition[field]
    }
  })

  if (!asArray(completed.options).length && asArray(extraDefinition?.options).length) {
    completed.options = extraDefinition.options
  }

  return completed
}

// Las definiciones llegan en task_data.surveys y en survey_items; cada fuente puede
// traer campos vacíos, así que se combinan por uid para no perder datos como las
// descripciones o las opciones de las preguntas anidadas.
export const buildSurveyDefinitions = (definitions = [], surveyItems = []) => {
  const definitionRows = [
    ...asArray(definitions),
    ...asArray(surveyItems).filter((item) => getDefinitionKey(item))
  ]
  const indexesByKey = new Map()
  const mergedDefinitions = []

  definitionRows.forEach((definitionRow) => {
    const key = getDefinitionKey(definitionRow)

    if (!key) {
      mergedDefinitions.push(definitionRow)
      return
    }

    if (indexesByKey.has(key)) {
      const existingIndex = indexesByKey.get(key)
      mergedDefinitions[existingIndex] = completeDefinition(
        mergedDefinitions[existingIndex],
        definitionRow
      )
      return
    }

    indexesByKey.set(key, mergedDefinitions.length)
    mergedDefinitions.push(definitionRow)
  })

  return mergedDefinitions
}

export const mergeSurveyReportData = ({
  definitions = [],
  responses = [],
  surveyItems = [],
  fallbackImages = []
} = {}) => {
  const safeDefinitions = buildSurveyDefinitions(definitions, surveyItems)
  const responseCandidates = [
    ...asArray(responses),
    ...asArray(surveyItems).filter(isAnsweredResponse)
  ]
  const usedResponses = new Set()

  return safeDefinitions.map((definition) => {
    const response = findResponse(definition, responseCandidates, usedResponses)
    const { selectedOptionIds, answerValues } = getAnswerValues(response || {})
    const rawAnswer = normalizeText(Array.isArray(response?.answer) ? '' : response?.answer)
    const wholeAnswerMatches =
      rawAnswer !== '' &&
      asArray(definition.options).some((option) => normalizeText(option.text) === rawAnswer)
    const candidateAnswers = wholeAnswerMatches ? [rawAnswer] : answerValues
    const imageAnswer = asArray(response?.image_answer)
    const selectedOptions = asArray(definition.options).map((option) => ({
      ...option,
      selected:
        selectedOptionIds.includes(option.id) ||
        candidateAnswers.some((answer) => normalizeText(answer) === normalizeText(option.text))
    }))

    return {
      ...definition,
      options: selectedOptions,
      descriptions:
        definition.descriptions || response?.descriptions || response?.description || '',
      answered: Boolean(response),
      answer: response?.answer ?? '',
      response: response?.answer ?? definition.response ?? '',
      image_answer:
        imageAnswer.length > 0
          ? imageAnswer
          : definition.type === 'image'
            ? asArray(fallbackImages)
            : [],
      selected_option_ids: selectedOptionIds,
      response_uid: response?.uid || response?.response_uid || null
    }
  })
}

// En el reporte solo se muestra la rama condicional de la opción que el usuario
// escogió en la app; las demás nunca se contestaron.
export const shouldShowConditionalOption = (option = {}, reportMode = false) => {
  if (!reportMode) return true

  return Boolean(option.selected)
}

const getDisplayIndexValue = (question = {}) =>
  question?.display_index || question?.displayIndex || ''

const parseDisplayIndexParts = (question = {}) => {
  const rawDisplayIndex = String(getDisplayIndexValue(question)).trim()

  if (!rawDisplayIndex) {
    return null
  }

  const parts = rawDisplayIndex.split('.').map((value) => Number(value))

  if (parts.some((value) => Number.isNaN(value))) {
    return null
  }

  return parts
}

const compareDisplayIndex = (a, b) => {
  const aParts = parseDisplayIndexParts(a)
  const bParts = parseDisplayIndexParts(b)

  if (!aParts && !bParts) {
    return 0
  }

  if (!aParts) {
    return 1
  }

  if (!bParts) {
    return -1
  }

  const maxLength = Math.max(aParts.length, bParts.length)

  for (let index = 0; index < maxLength; index += 1) {
    const aValue = aParts[index] || 0
    const bValue = bParts[index] || 0

    if (aValue !== bValue) {
      return aValue - bValue
    }
  }

  return 0
}

const getRootSiblingDisplayIndexNumber = (question = {}) => {
  const rawDisplayIndex = String(getDisplayIndexValue(question)).trim()

  if (!rawDisplayIndex || rawDisplayIndex.includes('.')) {
    return null
  }

  const siblingNumber = Number(rawDisplayIndex)
  return Number.isNaN(siblingNumber) ? null : siblingNumber
}

const resolveQuestionDisplayIndexes = (question = {}, resolvedDisplayIndex) => {
  return {
    ...question,
    resolved_display_index: resolvedDisplayIndex,
    options: (question.options || []).map((option, optionIndex) => {
      const childQuestions = option.childQuestions || []
      const hasMultipleChildren = childQuestions.length > 1

      return {
        ...option,
        childQuestions: childQuestions.map((childQuestion, childIndex) =>
          resolveQuestionDisplayIndexes(
            childQuestion,
            hasMultipleChildren
              ? `${resolvedDisplayIndex}.${optionIndex + 1}.${childIndex + 1}`
              : `${resolvedDisplayIndex}.${optionIndex + 1}`
          )
        )
      }
    })
  }
}

const resolveDisplayIndexes = (questions = []) => {
  const existingSiblingNumbers = questions
    .map((question) => getRootSiblingDisplayIndexNumber(question))
    .filter((value) => value !== null)

  let fallbackSiblingNumber = existingSiblingNumbers.length
    ? Math.max(...existingSiblingNumbers) + 1
    : 1

  return questions.map((question) => {
    const existingSiblingNumber = getRootSiblingDisplayIndexNumber(question)
    const resolvedDisplayIndex =
      existingSiblingNumber !== null ? `${existingSiblingNumber}` : `${fallbackSiblingNumber++}`

    return resolveQuestionDisplayIndexes(question, resolvedDisplayIndex)
  })
}

const sortQuestionsTree = (questions = []) =>
  [...questions].sort(compareDisplayIndex).map((question) => ({
    ...question,
    options: (question.options || []).map((option) => ({
      ...option,
      childQuestions: sortQuestionsTree(option.childQuestions || [])
    }))
  }))

const normalizeSurveyData = (flatSurveys = []) =>
  flatSurveys.map((survey) => ({
    ...survey,
    uid: survey?.uid || survey?.id || null,
    descriptions: survey?.descriptions || survey?.description || '',
    display_index: survey?.display_index || survey?.displayIndex || null,
    parent_survey_uid: survey?.parent_survey_uid || survey?.parentSurveyUid || null,
    parent_option_id: survey?.parent_option_id || survey?.parentOptionId || null,
    options: (survey?.options || []).map((option) => ({
      ...option,
      id: option?.id || option?.uid || null,
      text: option?.text ?? option?.label ?? '',
      childQuestions: []
    }))
  }))

export const buildSurveyTree = (flatSurveys = []) => {
  const clonedSurveys = normalizeSurveyData(flatSurveys)
  const surveysByUid = new Map()

  clonedSurveys.forEach((survey) => {
    if (survey.uid) {
      surveysByUid.set(survey.uid, survey)
    }
  })

  const rootQuestions = []

  clonedSurveys.forEach((survey) => {
    const hasParent = survey.parent_survey_uid && survey.parent_option_id

    if (!hasParent) {
      rootQuestions.push(survey)
      return
    }

    const parentSurvey = surveysByUid.get(survey.parent_survey_uid)

    if (!parentSurvey) {
      rootQuestions.push(survey)
      return
    }

    const parentOption = (parentSurvey.options || []).find(
      (option) => option.id === survey.parent_option_id
    )

    if (!parentOption) {
      rootQuestions.push(survey)
      return
    }

    parentOption.childQuestions.push(survey)
  })

  return resolveDisplayIndexes(sortQuestionsTree(rootQuestions))
}

const filterVisibleSurveyTree = (questions = [], reportMode = false) =>
  questions.map((question) => ({
    ...question,
    options: (question.options || []).map((option) => {
      const hasChildren = (option.childQuestions || []).length > 0
      const shouldShowChildren = hasChildren && shouldShowConditionalOption(option, reportMode)

      return {
        ...option,
        childQuestions: shouldShowChildren
          ? filterVisibleSurveyTree(option.childQuestions, reportMode)
          : []
      }
    })
  }))

const flattenSurveyTree = (questions = []) => {
  const rows = []

  const visit = (question, parentOptionText = null) => {
    rows.push({
      ...question,
      parent_option_text: parentOptionText || question.parent_option_text || null
    })
    ;(question.options || []).forEach((option) => {
      ;(option.childQuestions || []).forEach((childQuestion) =>
        visit(childQuestion, option.text)
      )
    })
  }

  questions.forEach((question) => visit(question))
  return rows
}

// Árbol del reporte, solo con las ramas que el usuario vio/contestó.
// El .n de cada condicional coincide con el número de la opción padre.
// Si una opción tiene varias hijas, se añade su posición: 1.1.1, 1.1.2.
export const getVisibleSurveyReportQuestions = (flatSurveys = [], reportMode = true) => {
  const visibleTree = filterVisibleSurveyTree(buildSurveyTree(flatSurveys), reportMode)
  return flattenSurveyTree(resolveDisplayIndexes(visibleTree))
}

const getSelectedOptionTexts = (survey = {}) =>
  asArray(survey.options)
    .filter((option) => option.selected)
    .map((option) => option.text)

const formatSurveyQuestionCsvTitle = (survey = {}, index = 0) => {
  const displayIndex = survey.resolved_display_index || survey.display_index || index + 1
  const parentOptionText = String(survey.parent_option_text || '').trim()
  const questionText = survey.question || ''

  if (parentOptionText) {
    return `PREGUNTA ${displayIndex} (Eligió: ${parentOptionText}): ${questionText}`
  }

  return `PREGUNTA ${displayIndex}: ${questionText}`
}

export const buildSurveyQuestionCsvRow = (survey = {}, index = 0) => {
  const questionRow = [formatSurveyQuestionCsvTitle(survey, index)]

  if (survey.type === 'multiplechk' || survey.type === 'multiplerb') {
    const selectedOptions = getSelectedOptionTexts(survey)

    if (selectedOptions.length === 0) {
      questionRow.push('No hay respuestas seleccionadas')
    } else {
      questionRow.push(...selectedOptions)
    }
  } else if (survey.type === 'image') {
    questionRow.push(
      asArray(survey.image_answer).length > 0
        ? asArray(survey.image_answer).join('\n')
        : survey.response || 'Sin respuesta'
    )
  } else {
    questionRow.push(survey.response || 'Sin respuesta')
  }

  if (asArray(survey.image_answer).length > 0 && survey.type !== 'image') {
    questionRow.push(asArray(survey.image_answer).join('\n'))
  }

  return questionRow
}
